import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import { SuccessResponse, useHttpClient } from "api/api"
import { CommandToSend, ScreeningNote, UserDevice } from "interface/device"
import { queryKeys as patientQueryKeys } from "services/patients"

export const queryKeys = {
	devices: "devices",
	logCommand: "log-command",
	screeningNote: "screening-note",
	upsertScreeningNote: "upsert-screening-note",
}

export const useLogCommand = () => {
	const { httpClient } = useHttpClient()
	type Payload = {
		deviceSerialNumber: string
		data: {
			command: CommandToSend
		}
	}

	return useMutation({
		mutationKey: [queryKeys.logCommand],
		mutationFn: (data: Payload) =>
			httpClient.post(`/api/v1/device-activities/command`, data),
	})
}

export const useDevices = (isLinked?: boolean) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.devices, isLinked],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<UserDevice[]>>(
				"/api/v1/devices/all",
				{
					signal,
					params: {
						isLinked,
					},
				},
			)

			return data
		},
	})
}

export const useScreeningNote = (
	deviceDataId: ScreeningNote["deviceDataId"],
) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.screeningNote, deviceDataId],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<ScreeningNote>>(
				`/api/v1/device-data/${deviceDataId}/clinical-note`,
				{ signal },
			)
			return data
		},
		placeholderData: keepPreviousData,
	})
}

export const useScreeningNoteUpsert = (
	deviceDataId: ScreeningNote["deviceDataId"],
	patientId: string,
) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.upsertScreeningNote],
		mutationFn: async (
			payload: Pick<ScreeningNote, "labels" | "observations" | "version">,
		) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<ScreeningNote>>(
				`/api/v1/device-data/${deviceDataId}/clinical-note`,
				payload,
			)
			return data
		},
		onSuccess(note) {
			queryClient.setQueryData<ScreeningNote>(
				[queryKeys.screeningNote, deviceDataId],
				note,
			)
			queryClient.invalidateQueries({
				queryKey: [patientQueryKeys.timelineScreenings, patientId],
			})
			queryClient.invalidateQueries({
				queryKey: [patientQueryKeys.timeline, patientId],
			})
		},
	})
}
